import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { createTheme } from '@mui/material/styles';

const THEME_STORAGE_KEY = 'autoapp_theme_mode';

// ─── Color palettes ──────────────────────────────────────────
export const lightColors = {
  primary: '#007ca2',
  primaryDark: '#005f7f',
  primaryDarker: '#004d66',
  primaryLight: '#009bc7',
  secondary: '#6b7280',
  accent: '#00A7E1',
  text: { primary: '#1a1a1a', secondary: '#6b7280' },
  textDark: '#1A1A1A',
  textSecondary: '#6B7A8D',
  textMuted: '#9EAAB8',
  error: '#f44336',
  errorDark: '#d32f2f',
  errorLight: '#FEE2E2',
  success: '#4caf50',
  successLight: '#D1FAE5',
  warning: '#f59e0b',
  disabled: '#9CA3AF',
  background: { default: '#f9fafb', paper: '#ffffff', page: '#f9fafb' },
  surface: '#FFFFFF',
  inputBackground: '#F1F5F9',
  border: '#e0e0e0',
  borderLight: '#E0E7EF',
  // Additional
  orange: '#FF6B35',
  gold: '#FFD700',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  gray: '#888888',
  darkGray: '#333333',
  lightGray: '#F6FAFD',
  lightGray2: '#F0F4F8',
  // NavBar
  navBar: {
    gradient: '#007ca2',
    hoverBg: 'rgba(255,255,255,0.1)',
    activeBg: 'rgba(255,255,255,0.2)',
    borderActive: 'rgba(255,255,255,0.3)',
    text: '#ffffff',
    menuBg: '#ffffff',
    menuText: '#2c3e50',
    menuHover: 'rgba(0,124,162,0.08)',
    menuShadow: '0 8px 24px rgba(0, 40, 60, 0.1)',
    menuBorder: 'rgba(0,124,162,0.2)',
  },
  // Countdown
  countdown: {
    containerBg: '#ffffff',
    blockBg: '#e0e0e0',
    number: '#007ca2',
    label: '#000000',
    finish: '#f44336',
    shadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
    blockShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  },
  // Gradients (buttons & decorative bars only)
  gradients: {
    primary: 'linear-gradient(135deg, #007ca2 0%, #005f7f 100%)',
    primaryHover: 'linear-gradient(135deg, #009bc7 0%, #007ca2 100%)',
    primaryTriple: 'linear-gradient(90deg, #007ca2, #005f7f, #004d66)',
    paper: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
    imageBox: 'linear-gradient(135deg, rgba(0,124,162,0.05) 0%, rgba(0,95,127,0.02) 100%)',
  },
  // Flat gradient strings (buttons & bars — NOT for text)
  gradientPrimary: 'linear-gradient(135deg, #007CA2 0%, #005F7A 100%)',
  gradientPrimaryHover: 'linear-gradient(135deg, #00A0D2 0%, #007CA2 100%)',
  gradientPrimaryDarker: 'linear-gradient(90deg, #007CA2, #005F7A, #004D66)',
  gradientAccent: 'linear-gradient(135deg, #00A7E1 0%, #0081AF 100%)',
  gradientGray: 'linear-gradient(135deg, #888888 0%, #AAAAAA 100%)',
  // Aliases (backward compat — map to core tokens)
  primaryHover: '#009bc7',
  primaryVariant: '#005f7f',
  accentLight: '#4dd8f7',
  secondaryBlue: '#0077B6',
  secondaryBlueLight: '#48cae4',
  gradientAccentHover: 'linear-gradient(135deg, #00A7E1 0%, #007ca2 100%)',
  gradientLightAccent: 'linear-gradient(135deg, rgba(0,167,225,0.08) 0%, rgba(0,124,162,0.04) 100%)',
  shadows: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.2)',
    md: '0 4px 12px rgba(0, 124, 162, 0.2)',
    lg: '0 4px 16px rgba(0, 124, 162, 0.08)',
    xlg: '0 8px 24px rgba(0, 124, 162, 0.12)',
    primary: '0 8px 24px rgba(0, 124, 162, 0.3)',
    primaryHover: '0 12px 32px rgba(0, 124, 162, 0.4)',
    box: '0 20px 40px rgba(0, 124, 162, 0.1)',
  },
  // react-select
  select: {
    controlBg: '#fff',
    controlBorder: '#e0e0e0',
    menuBg: '#fff',
    optionText: '#1a1a1a',
    placeholder: '#9ca3af',
    singleValue: '#1a1a1a',
  },
  // Form-specific
  formContainer: 'transparent',
  sectionBorder: 'rgba(0, 124, 162, 0.1)',
  imageBorder: 'rgba(0, 124, 162, 0.2)',
};

export const darkColors = {
  primary: '#00b4d8',
  primaryDark: '#007ca2',
  primaryDarker: '#005f7f',
  primaryLight: '#48cae4',
  secondary: '#9aa0a6',
  accent: '#48cae4',
  text: { primary: '#e8eaed', secondary: '#9aa0a6' },
  textDark: '#e8eaed',
  textSecondary: '#9aa0a6',
  textMuted: '#6b7280',
  error: '#ff6b6b',
  errorDark: '#ef5350',
  errorLight: 'rgba(255,107,107,0.15)',
  success: '#4ade80',
  successLight: 'rgba(74,222,128,0.15)',
  warning: '#fbbf24',
  disabled: '#6b7280',
  background: { default: '#121212', paper: '#1e1e1e', page: '#121212' },
  surface: '#1e1e1e',
  inputBackground: '#2a2d31',
  border: '#333333',
  borderLight: '#333333',
  // Additional
  orange: '#FF6B35',
  gold: '#FFD700',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  gray: '#888888',
  darkGray: '#e0e0e0',
  lightGray: '#1e1e1e',
  lightGray2: '#2a2d31',
  // NavBar
  navBar: {
    gradient: '#1a1a1a',
    hoverBg: 'rgba(255,255,255,0.08)',
    activeBg: 'rgba(255,255,255,0.12)',
    borderActive: 'rgba(255,255,255,0.2)',
    text: '#e8eaed',
    menuBg: '#2a2a2a',
    menuText: '#e8eaed',
    menuHover: 'rgba(0,180,216,0.12)',
    menuShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    menuBorder: 'rgba(255,255,255,0.1)',
  },
  // Countdown
  countdown: {
    containerBg: '#1e1e1e',
    blockBg: '#2a2d31',
    number: '#00b4d8',
    label: '#e8eaed',
    finish: '#ff6b6b',
    shadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
    blockShadow: '0px 2px 4px rgba(0, 0, 0, 0.4)',
  },
  // Gradients (buttons & decorative bars only)
  gradients: {
    primary: 'linear-gradient(135deg, #00b4d8 0%, #007ca2 100%)',
    primaryHover: 'linear-gradient(135deg, #48cae4 0%, #00b4d8 100%)',
    primaryTriple: 'linear-gradient(90deg, #00b4d8, #007ca2, #005f7f)',
    paper: 'linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(42,45,49,0.98) 100%)',
    imageBox: 'linear-gradient(135deg, rgba(0,180,216,0.08) 0%, rgba(0,124,162,0.04) 100%)',
  },
  // Flat gradient strings (buttons & bars — NOT for text)
  gradientPrimary: 'linear-gradient(135deg, #00b4d8 0%, #007ca2 100%)',
  gradientPrimaryHover: 'linear-gradient(135deg, #48cae4 0%, #00b4d8 100%)',
  gradientPrimaryDarker: 'linear-gradient(90deg, #00b4d8, #007ca2, #005f7f)',
  gradientAccent: 'linear-gradient(135deg, #48cae4 0%, #00b4d8 100%)',
  gradientGray: 'linear-gradient(135deg, #555555 0%, #777777 100%)',
  // Aliases (backward compat — map to core tokens)
  primaryHover: '#48cae4',
  primaryVariant: '#007ca2',
  accentLight: '#90e0ef',
  secondaryBlue: '#0077B6',
  secondaryBlueLight: '#48cae4',
  gradientAccentHover: 'linear-gradient(135deg, #48cae4 0%, #00b4d8 100%)',
  gradientLightAccent: 'linear-gradient(135deg, rgba(0,180,216,0.12) 0%, rgba(0,124,162,0.06) 100%)',
  shadows: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.4)',
    md: '0 4px 12px rgba(0, 0, 0, 0.3)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.2)',
    xlg: '0 8px 24px rgba(0, 0, 0, 0.3)',
    primary: '0 8px 24px rgba(0, 180, 216, 0.2)',
    primaryHover: '0 12px 32px rgba(0, 180, 216, 0.3)',
    box: '0 20px 40px rgba(0, 0, 0, 0.3)',
  },
  // react-select
  select: {
    controlBg: '#2a2d31',
    controlBorder: '#444',
    menuBg: '#2a2a2a',
    optionText: '#e8eaed',
    placeholder: '#6b7280',
    singleValue: '#e8eaed',
  },
  // Form-specific
  formContainer: 'transparent',
  sectionBorder: 'rgba(255, 255, 255, 0.08)',
  imageBorder: 'rgba(255, 255, 255, 0.15)',
};

// ─── Context ─────────────────────────────────────────────────
const ThemeContext = createContext({
  colors: lightColors,
  isDark: false,
  themeMode: 'system',
  setThemeMode: () => {},
  muiTheme: null,
});

// ─── Build MUI theme from palette ───────────────────────────
const buildMuiTheme = (isDark, colors) =>
  createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: { main: colors.primary },
      secondary: { main: '#11cb5f' },
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
      },
      error: { main: colors.error },
      success: { main: colors.success },
      warning: { main: colors.warning },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderBottom: 'none',
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none', // Remove MUI's default dark paper overlay
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: colors.border,
          },
        },
      },
    },
  });

// ─── Provider ────────────────────────────────────────────────
export const AppThemeProvider = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(() => {
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) || 'dark';
    } catch {
      return 'dark';
    }
  });

  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {}
  }, []);

  const systemDark = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = () => {
      if (themeMode === 'system') {
        // force re-render (state already 'system', we tweak a dummy)
        setThemeModeState('system');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeMode]);

  const isDark = useMemo(() => {
    if (themeMode === 'dark') return true;
    if (themeMode === 'light') return false;
    return systemDark;
  }, [themeMode, systemDark]);

  const colors = isDark ? darkColors : lightColors;
  const muiTheme = useMemo(() => buildMuiTheme(isDark, colors), [isDark, colors]);

  // Set CSS custom properties for SCSS files
  useEffect(() => {
    const root = document.documentElement;
    const isLanding = document.body.classList.contains('landing-page-active');
    root.style.setProperty('--bg-page', isLanding ? '#080808' : colors.background.page);
    root.style.setProperty('--countdown-container-bg', colors.countdown.containerBg);
    root.style.setProperty('--countdown-block-bg', colors.countdown.blockBg);
    root.style.setProperty('--countdown-number', colors.countdown.number);
    root.style.setProperty('--countdown-label', colors.countdown.label);
    root.style.setProperty('--countdown-finish', colors.countdown.finish);
    root.style.setProperty('--countdown-shadow', colors.countdown.shadow);
    root.style.setProperty('--countdown-block-shadow', colors.countdown.blockShadow);
    root.style.setProperty('--text-primary', colors.text.primary);
    root.style.setProperty('--link-color', isDark ? colors.primary : 'white');
  }, [colors, isDark]);

  const value = useMemo(
    () => ({ colors, isDark, themeMode, setThemeMode, muiTheme }),
    [colors, isDark, themeMode, setThemeMode, muiTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useAppTheme = () => useContext(ThemeContext);
export { ThemeContext };
